import type { MouseEvent, ReactNode } from 'react';

import { cn } from '../lib/utils';

type Tone = 'quiet' | 'danger';

/**
 * How big the square is. `default` is the 48px control height, so it lines up
 * in a toolbar; `compact` is the 44px minimum target, for controls laid over
 * something they should not hide much of, like the seat map's artwork.
 */
type Size = 'default' | 'compact';

const tones: Record<Tone, string> = {
  quiet: 'border-hairline bg-surface text-ink-muted hover:border-brand-edge hover:text-brand-text',
  danger:
    'border-btn-danger-edge bg-btn-danger-bg text-btn-danger-fg hover:bg-btn-danger-hover-bg hover:text-btn-danger-hover-fg',
};

const sizes: Record<Size, string> = {
  default: 'h-(--target-control) w-12',
  compact: 'size-(--target-min)',
};

/**
 * Icon-only control. Never smaller than the 44px minimum target, and `label`
 * is mandatory: it supplies both the tooltip and the accessible name, since an
 * icon alone tells a low-vision reader nothing.
 */
export default function IconButton({
  label,
  onClick,
  tone = 'quiet',
  size = 'default',
  disabled,
  children,
  className,
  'aria-invalid': ariaInvalid,
}: {
  label: string;
  /** Handed the event, so a button inside a clickable row can stop it there. */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  tone?: Tone;
  size?: Size;
  disabled?: boolean;
  children: ReactNode;
  className?: string;
  /** Marks the control that opens a fix, so a form's Save can move focus to it. */
  'aria-invalid'?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-invalid={ariaInvalid}
      title={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-button border transition-colors duration-(--duration-fast) ease-standard focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)',
        // No pointer events while disabled, so the tone's hover cannot fire either.
        'disabled:pointer-events-none disabled:opacity-55',
        sizes[size],
        tones[tone],
        className,
      )}
    >
      {children}
    </button>
  );
}
